import type { FormatFamily } from './formats'

/**
 * Region-of-interest geometry for the decode loop.
 *
 * Three jobs, all of which the previous app got wrong or didn't attempt:
 *
 *  1. **Shape the crop to the symbol.** 2D codes sit in a square near the
 *     centre; 1D codes are wide and short. html5-qrcode used one square box for
 *     everything, so a barcode wide enough to read was always partly outside it.
 *
 *  2. **Provide zoom without a camera zoom.** iOS exposes no `zoom` capability
 *     to any browser, so "2×" here means cropping a smaller region out of a
 *     high-resolution frame and decoding that. On a 1080p capture a 2× crop
 *     still leaves ~540px of real detail — plenty, and it works identically on
 *     every platform because it never touches the camera.
 *
 *  3. **Cap what gets decoded.** Decoding a full 1080p frame every tick is what
 *     drives the memory churn that gets a tab killed on iOS. The crop is scaled
 *     down to a budget before it ever reaches the decoder.
 */

export interface Roi {
  /** Source rectangle in video pixels. */
  sx: number
  sy: number
  sw: number
  sh: number
  /** Destination size handed to the decoder, after downscaling to budget. */
  dw: number
  dh: number
  /** Scale from destination pixels back to video pixels, for the overlay. */
  scale: number
}

export interface RoiOptions {
  /**
   * Fraction of the short edge covered by the 2D box at 1× zoom.
   *
   * Deliberately generous. A tight reticle looks precise and forces the user to
   * aim, which is exactly the "constantly manoeuvring the camera" complaint.
   */
  boxFraction?: number
  /** Fraction of the long edge covered by the 1D band at 1× zoom. */
  bandFraction?: number
  /** Height of the 1D band as a fraction of its width. */
  bandAspect?: number
  /** Longest edge, in pixels, handed to the decoder. */
  maxDecodeEdge?: number
}

const DEFAULTS: Required<RoiOptions> = {
  boxFraction: 0.82,
  bandFraction: 0.92,
  // 1D symbols only need enough rows to find a few consistent scan lines;
  // decoding a tall band is wasted work.
  bandAspect: 0.4,
  maxDecodeEdge: 900,
}

export const MIN_ZOOM = 1
export const MAX_ZOOM = 5

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM)
}

/**
 * Compute the crop and decode size for one family at a given zoom.
 *
 * `videoW`/`videoH` are the intrinsic dimensions of the video frame, not the
 * CSS size of the element — they differ whenever the preview is letterboxed or
 * cropped by `object-fit`, and using the wrong one silently misaligns the
 * overlay from what was actually decoded.
 */
export function computeRoi(
  family: FormatFamily,
  videoW: number,
  videoH: number,
  zoom = 1,
  options: RoiOptions = {},
): Roi {
  const o = { ...DEFAULTS, ...options }
  const z = clampZoom(zoom)

  // A frame with no dimensions yet (video not ready) must not produce NaNs
  // that propagate into canvas calls and throw.
  if (!(videoW > 0) || !(videoH > 0)) {
    return { sx: 0, sy: 0, sw: 0, sh: 0, dw: 0, dh: 0, scale: 1 }
  }

  const shortEdge = Math.min(videoW, videoH)
  const longEdge = Math.max(videoW, videoH)

  let sw: number
  let sh: number

  if (family === '2d') {
    const side = (shortEdge * o.boxFraction) / z
    sw = side
    sh = side
  } else {
    // The band follows the frame's own orientation: on a phone held upright the
    // video is portrait, and a band across the *long* edge would be vertical —
    // useless for a horizontal barcode. Width always tracks the wider axis.
    const bandW = (longEdge * o.bandFraction) / z
    const bandH = bandW * o.bandAspect
    if (videoW >= videoH) {
      sw = bandW
      sh = bandH
    } else {
      // Portrait frame: the barcode is still horizontal on screen, so the band
      // is limited by the frame width rather than its height.
      sw = (videoW * o.bandFraction) / z
      sh = sw * o.bandAspect
    }
  }

  sw = Math.min(sw, videoW)
  sh = Math.min(sh, videoH)

  const sx = Math.max(0, Math.round((videoW - sw) / 2))
  const sy = Math.max(0, Math.round((videoH - sh) / 2))
  sw = Math.round(sw)
  sh = Math.round(sh)

  // Downscale to the decode budget. Never upscale: inventing pixels cannot
  // recover detail and only makes the decoder work harder.
  const longest = Math.max(sw, sh)
  const scale = longest > o.maxDecodeEdge ? o.maxDecodeEdge / longest : 1

  return {
    sx,
    sy,
    sw,
    sh,
    dw: Math.max(1, Math.round(sw * scale)),
    dh: Math.max(1, Math.round(sh * scale)),
    scale,
  }
}

/**
 * Map a decoded point from decode-space back to video-space.
 *
 * The overlay draws where the code actually is on screen. Without this the
 * highlight lands in the top-left corner at 1× and drifts further out the more
 * you zoom, because the decoder only ever saw the cropped, downscaled image.
 */
export function roiPointToVideo(roi: Roi, x: number, y: number): { x: number; y: number } {
  return {
    x: roi.sx + x / (roi.scale || 1),
    y: roi.sy + y / (roi.scale || 1),
  }
}

/**
 * Fraction of the frame the ROI covers — used to tell the user whether to move
 * closer, rather than leaving them to guess why nothing is happening.
 */
export function roiCoverage(roi: Roi, videoW: number, videoH: number): number {
  if (!(videoW > 0) || !(videoH > 0)) return 0
  return (roi.sw * roi.sh) / (videoW * videoH)
}
